import { fork } from '@cortex/utils/fork';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useEffect, useState } from 'react';

import { BlueprintForm, emptyBlueprintInput } from '../../../components/blueprints/BlueprintForm';
import { BackButton } from '../../../components/common/BackButton';
import { EmptyState } from '../../../components/common/EmptyState';
import { PageHeader } from '../../../components/common/PageHeader';
import { Spinner } from '../../../components/common/Spinner';
import { useMcpActivityEffect } from '../../../hooks/useMcpActivity';
import { routes } from '../../../lib/constants';
import {
  useMetaphysics,
  type IBlueprint,
  type IBlueprintCreateInput,
} from '../../../lib/metaphysics';
import { guardSuperuser } from '../index';

/** Extract `blueprintId` from a tool-call's `args` payload, when present. */
function argBlueprintId(args: unknown): string | null {
  if (args && typeof args === 'object' && 'blueprintId' in args) {
    const v = (args as { blueprintId?: unknown }).blueprintId;
    return typeof v === 'string' ? v : null;
  }
  return null;
}

export const Route = createFileRoute('/internal/blueprints/$blueprintId')({
  beforeLoad: () => guardSuperuser(),
  component: BlueprintDetailPage,
});

function toInput(b: IBlueprint): IBlueprintCreateInput {
  return {
    name: b.name,
    skillsRepoUrl: b.skillsRepoUrl ?? null,
    prompts: (b.prompts ?? []).map((p) => ({ name: p.name, content: p.content })),
    targetArtifacts: (b.targetArtifacts ?? []).map((t) => ({
      name: t.name,
      artifactType: t.artifactType,
      description: t.description ?? null,
      isSuggestion: !!t.isSuggestion,
    })),
  };
}

function BlueprintDetailPage() {
  const { blueprintId } = Route.useParams();
  const mp = useMetaphysics();
  const qc = useQueryClient();
  const nav = useNavigate();

  const blueprint = useQuery({
    queryKey: ['mp', 'blueprint', blueprintId],
    queryFn: () => mp.getBlueprint(blueprintId),
  });

  // Local draft + baseline (for dirty detection). Initialised once the
  // server data lands; later refetches don't clobber unsaved edits.
  const [draft, setDraft] = useState<IBlueprintCreateInput>(emptyBlueprintInput);
  const [baseline, setBaseline] = useState<string>('');

  useEffect(() => {
    if (blueprint.data && !baseline) {
      const next = toInput(blueprint.data);
      setDraft(next);
      setBaseline(JSON.stringify(next));
    }
  }, [blueprint.data, baseline]);

  // Refetch when the chat agent updates THIS blueprint through the chat.
  // We can't just clear `baseline` and rely on the seeding effect above —
  // that effect would run against the *stale* `blueprint.data` first
  // (before the refetch lands) and re-seed with old data, then skip the
  // refetched data. Instead: await the refetch, then explicitly hydrate
  // both `draft` and `baseline` from the fresh cache value.
  useMcpActivityEffect(
    (e) =>
      e.status === 'ok' && e.name === 'update_blueprint' && argBlueprintId(e.args) === blueprintId,
    () => {
      fork(qc.invalidateQueries({ queryKey: ['mp', 'blueprints'] }));
      fork(
        (async () => {
          await qc.invalidateQueries({ queryKey: ['mp', 'blueprint', blueprintId] });
          const fresh = qc.getQueryData<IBlueprint>(['mp', 'blueprint', blueprintId]);
          if (!fresh) return;
          const next = toInput(fresh);
          setDraft(next);
          setBaseline(JSON.stringify(next));
        })(),
      );
    },
  );

  // If chat deletes this blueprint, bounce the user back to the list so
  // they don't sit on a stale detail page that's about to 404.
  useMcpActivityEffect(
    (e) =>
      e.status === 'ok' && e.name === 'delete_blueprint' && argBlueprintId(e.args) === blueprintId,
    () => {
      fork(qc.invalidateQueries({ queryKey: ['mp', 'blueprints'] }));
      fork(nav({ to: routes.INTERNAL_BLUEPRINTS }));
    },
  );

  const dirty = baseline !== '' && JSON.stringify(draft) !== baseline;

  const save = useMutation({
    mutationFn: () => mp.updateBlueprint(blueprintId, draft),
    onSuccess: async (b) => {
      const next = toInput(b);
      setDraft(next);
      setBaseline(JSON.stringify(next));
      try {
        await qc.invalidateQueries({ queryKey: ['mp', 'blueprints'] });
        await qc.invalidateQueries({ queryKey: ['mp', 'blueprint', blueprintId] });
      } catch (err) {
        console.error(err);
      }
    },
  });

  const remove = useMutation({
    mutationFn: () => mp.deleteBlueprint(blueprintId),
    onSuccess: async () => {
      try {
        await qc.invalidateQueries({ queryKey: ['mp', 'blueprints'] });
      } catch (err) {
        console.error(err);
      }
      try {
        await nav({ to: routes.INTERNAL_BLUEPRINTS });
      } catch (err) {
        console.error(err);
      }
    },
  });

  if (blueprint.isLoading) return <Spinner />;
  if (blueprint.error)
    return (
      <div className="alert alert--danger">
        Failed to load: {(blueprint.error as Error).message}
      </div>
    );
  if (!blueprint.data) return <EmptyState title="Not found" />;

  const canSave = draft.name.trim().length > 0 && dirty && !save.isPending;

  return (
    <>
      <PageHeader
        title={blueprint.data.name}
        actions={
          <>
            <BackButton to={routes.INTERNAL_BLUEPRINTS} label="Blueprints" />
            <button
              type="button"
              className="btn btn--destructive btn--sm"
              disabled={remove.isPending}
              onClick={() => {
                if (
                  confirm(
                    `Delete blueprint "${blueprint.data?.name}"? This fails if any pipeline still uses it.`,
                  )
                )
                  remove.mutate();
              }}
            >
              {remove.isPending ? 'Deleting…' : 'Delete'}
            </button>
          </>
        }
      />
      <div className="card" style={{ maxWidth: 880 }}>
        <div className="card-body">
          <form
            onSubmit={(e) => {
              e.preventDefault();
              if (canSave) save.mutate();
            }}
            style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-3)' }}
          >
            <BlueprintForm value={draft} onChange={setDraft} />
            {save.error ? (
              <div className="alert alert--danger">{(save.error as Error).message}</div>
            ) : null}
            {remove.error ? (
              <div className="alert alert--danger">{(remove.error as Error).message}</div>
            ) : null}
            <div
              style={{
                display: 'flex',
                gap: 'var(--sp-2)',
                justifyContent: 'flex-end',
                alignItems: 'center',
              }}
            >
              {dirty ? <span className="t-xs t-muted">Unsaved changes</span> : null}
              <button type="submit" className="btn btn--primary" disabled={!canSave}>
                {save.isPending ? 'Saving…' : 'Save changes'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </>
  );
}
